/**
 * Layer Wrapper Components
 * 
 * Provides wrapper components for conditional rendering based on layer states.
 * These components handle mounting, interaction, and display of UI layers
 * according to the global layer state management system.
 */

import React, { useMemo } from 'react';
import { useLayerState } from '../hooks/useLayerState';
import { LayerWrapperProps } from '../types/layerState';
import { LayerId } from '../styles/styles-data/zIndex';

/**
 * Base LayerWrapper component for conditional rendering
 * 
 * @example
 * ```tsx
 * <LayerWrapper layerId="overlays" activeClassName="overlay-active">
 *   <ProjectOverlay />
 * </LayerWrapper>
 * ```
 */
export const LayerWrapper: React.FC<LayerWrapperProps> = ({
  layerId,
  children,
  activeClassName = '',
  inactiveClassName = '',
  render,
}) => {
  const { state, isActive, isVisible, isRendered } = useLayerState(layerId);

  // Determine className based on layer state
  const className = useMemo(() => {
    const classes = [];
    if (isActive && activeClassName) classes.push(activeClassName);
    if (!isActive && inactiveClassName) classes.push(inactiveClassName);
    return classes.join(' ');
  }, [isActive, activeClassName, inactiveClassName]);

  // Apply appropriate styling and interaction states
  const wrapperStyle = useMemo(() => ({
    display: isVisible ? 'block' : 'none',
    pointerEvents: (state.interactive ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
  }), [isVisible, state.interactive]);

  // Use custom render function if provided
  if (render) {
    return <>{render(state)}</>;
  }

  // Don't render anything if layer is not supposed to be in DOM
  if (!isRendered) {
    return null;
  }

  return (
    <div
      className={className}
      style={wrapperStyle}
      data-layer-id={layerId}
      data-layer-rendered={isRendered}
      data-layer-interactive={state.interactive}
      data-layer-displayed={isVisible}
    >
      {children}
    </div>
  );
};

/**
 * Specialized wrapper components for each layer
 */

/**
 * Base Content Layer (Layer 0)
 */
export const BaseContentLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="base" {...props} />
);

/**
 * Background Effects Layer (Layer 1)
 */
export const BackgroundLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="background" {...props} />
);

/**
 * Main Content Layer (Layer 2)
 */
export const MainContentLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="mainContent" {...props} />
);

/**
 * Navigation Layer (Layer 3)
 */
export const NavigationLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="navigation" {...props} />
);

/**
 * Overlay Layer (Layer 4)
 */
export const OverlayLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="overlays" {...props} />
);

/**
 * Account & API Layer (Layer 5)
 */
export const AccountApiLayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="accountApi" {...props} />
);

/**
 * System UI Layer (Layer 6)
 */
export const SystemUILayer: React.FC<Omit<LayerWrapperProps, 'layerId'>> = (props) => (
  <LayerWrapper layerId="systemUI" {...props} />
);

/**
 * Conditional rendering component based on layer state
 */
export const ConditionalLayer: React.FC<{
  layerId: LayerId;
  when: 'rendered' | 'displayed' | 'interactive' | 'active';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ layerId, when, children, fallback = null }) => {
  const { state, isActive } = useLayerState(layerId);

  const shouldRender = useMemo(() => {
    switch (when) {
      case 'rendered':
        return state.rendered;
      case 'displayed':
        return state.displayed;
      case 'interactive':
        return state.interactive;
      case 'active':
        return isActive;
      default:
        return false;
    }
  }, [state, isActive, when]);

  return <>{shouldRender ? children : fallback}</>;
};

/**
 * Layer-aware component that provides layer state to its children
 */
export const LayerProvider: React.FC<{
  layerId: LayerId;
  children: (state: ReturnType<typeof useLayerState>) => React.ReactNode;
}> = ({ layerId, children }) => {
  const layerState = useLayerState(layerId);
  return <>{children(layerState)}</>;
};

/**
 * Debug component that shows layer information (development only)
 */
export const LayerDebugInfo: React.FC<{ layerId: LayerId }> = ({ layerId }) => {
  const { state, isActive, isVisible } = useLayerState(layerId);

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const statusColor = isActive ? '#22c55e' : isVisible ? '#eab308' : '#ef4444';
  const statusText = isActive ? 'ACTIVE' : isVisible ? 'VISIBLE' : 'INACTIVE';

  return (
    <div
      style={{
        position: 'fixed',
        top: 10,
        right: 10,
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        border: `2px solid ${statusColor}`,
      }}
    >
      <div><strong>{layerId.toUpperCase()}</strong></div>
      <div style={{ color: statusColor }}>{statusText}</div>
      <div>R:{state.rendered ? '✓' : '✗'} I:{state.interactive ? '✓' : '✗'} D:{state.displayed ? '✓' : '✗'}</div>
    </div>
  );
};

/**
 * Transition wrapper that handles layer state changes with animations
 */
export const TransitionLayerWrapper: React.FC<LayerWrapperProps & {
  transitionDuration?: number;
  transitionType?: 'fade' | 'slide' | 'scale';
}> = ({
  layerId,
  children,
  activeClassName = '',
  inactiveClassName = '',
  transitionDuration = 300,
  transitionType = 'fade',
}) => {
  const { state, isActive, isVisible, isRendered } = useLayerState(layerId);

  const transitionStyle = useMemo(() => {
    const baseTransition = `all ${transitionDuration}ms ease-in-out`;
    
    switch (transitionType) {
      case 'fade':
        return {
          transition: baseTransition,
          opacity: isVisible ? 1 : 0,
          pointerEvents: (state.interactive ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
        };
      case 'slide':
        return {
          transition: baseTransition,
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          pointerEvents: (state.interactive ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
        };
      case 'scale':
        return {
          transition: baseTransition,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: (state.interactive ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
        };
      default:
        return {
          transition: baseTransition,
          pointerEvents: (state.interactive ? 'auto' : 'none') as React.CSSProperties['pointerEvents'],
        };
    }
  }, [isVisible, state.interactive, transitionDuration, transitionType]);

  const className = useMemo(() => {
    const classes = [];
    if (isActive && activeClassName) classes.push(activeClassName);
    if (!isActive && inactiveClassName) classes.push(inactiveClassName);
    return classes.join(' ');
  }, [isActive, activeClassName, inactiveClassName]);

  // Don't render if not supposed to be in DOM
  if (!isRendered) {
    return null;
  }

  return (
    <div
      className={className}
      style={transitionStyle}
      data-layer-id={layerId}
      data-layer-rendered={isRendered}
      data-layer-interactive={state.interactive}
      data-layer-displayed={isVisible}
    >
      {children}
    </div>
  );
};